import React, { useCallback, useEffect, useState } from 'react';
import Loading from '../../components/common/Loading/Loading';
import FormSelect from '../../components/common/FormSelect/FormSelect';
import FormInput from '../../components/common/FormInput/FormInput';
import { SubscriptionPlanService } from '../../services';
import { ChargeCheckoutService } from '../../services/ChargeCheckoutService';
import PlanNameWithProject, { createPlanSelectFormatters } from '../../components/common/PlanNameWithProject/PlanNameWithProject';
import { SubscriptionPlan } from '../../types';
import type { ProjectIdentity } from '../../types/project';
import { formatCurrency } from '../../utils';
import { buildChargeCheckoutUrl } from '../../utils/checkoutPublicLinks';
import { CYCLE_LABELS } from '../../components/students/PaymentDetails/paymentDetails.constants';
import Tooltip from '../../components/common/Tooltip';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';
import styles from './CommercialCheckoutLinkModal.module.css';

export interface ChargeCheckoutLockedPlanSummary {
  name: string;
  priceCents: number;
  billingCycle: string;
  isSinglePayment: boolean;
  project?: ProjectIdentity | null;
}

interface ChargeCheckoutLinkModalProps {
  open: boolean;
  studentName: string;
  studentId: number;
  initialPlanId?: number | null;
  /** When true, uses the subscription plan and hides the plan selector. */
  lockPlan?: boolean;
  lockedPlan?: ChargeCheckoutLockedPlanSummary | null;
  onClose: () => void;
}

const ChargeCheckoutLinkModal: React.FC<ChargeCheckoutLinkModalProps> = ({
  open,
  studentName,
  studentId,
  initialPlanId = null,
  lockPlan = false,
  lockedPlan = null,
  onClose,
}) => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [checkoutUrl, setCheckoutUrl] = useState('');
  const [urlLoading, setUrlLoading] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [linkMode, setLinkMode] = useState<'new_charge' | 'pay_existing'>('new_charge');
  const [openPaymentValue, setOpenPaymentValue] = useState<number | null>(null);
  const [isCopying, setIsCopying] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedPlanId('');
      setCheckoutUrl('');
      setLinkError(null);
      setLinkMode('new_charge');
      setOpenPaymentValue(null);
      setIsCopied(false);
      return;
    }

    if (lockPlan) {
      if (!initialPlanId) {
        setSelectedPlanId('');
        setLinkError('Não há plano na assinatura para gerar o link de pagamento.');
        setPlansLoading(false);
        return;
      }

      setSelectedPlanId(String(initialPlanId));
      setPlansLoading(false);
      return;
    }

    setPlansLoading(true);
    SubscriptionPlanService.getSelectablePlans()
      .then((loadedPlans) => {
        setPlans(loadedPlans);
        if (initialPlanId && loadedPlans.some((plan) => plan.id === initialPlanId)) {
          setSelectedPlanId(String(initialPlanId));
        }
      })
      .finally(() => setPlansLoading(false));
  }, [initialPlanId, lockPlan, open]);

  useEffect(() => {
    if (!open || !selectedPlanId) {
      setCheckoutUrl('');
      setLinkMode('new_charge');
      setOpenPaymentValue(null);
      if (!lockPlan || initialPlanId) {
        setLinkError(null);
      }
      return;
    }

    let cancelled = false;
    setUrlLoading(true);
    setLinkError(null);

    ChargeCheckoutService.generateLink(studentId, Number(selectedPlanId))
      .then((link) => {
        if (cancelled) {
          return;
        }

        setCheckoutUrl(buildChargeCheckoutUrl(link.planHash, link.token));
        setLinkMode(link.mode === 'pay_existing' ? 'pay_existing' : 'new_charge');
        setOpenPaymentValue(
          typeof link.openPaymentValue === 'number' && Number.isFinite(link.openPaymentValue)
            ? link.openPaymentValue
            : null,
        );
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setCheckoutUrl('');
          setLinkMode('new_charge');
          setOpenPaymentValue(null);
          setLinkError(
            error instanceof Error && error.message.trim()
              ? error.message.trim()
              : 'Não foi possível gerar o link de pagamento.',
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setUrlLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [initialPlanId, lockPlan, open, selectedPlanId, studentId]);

  useEscapeToClose(onClose, open);

  const handleCopy = useCallback(async () => {
    if (!checkoutUrl || isCopying) {
      return;
    }

    setIsCopying(true);

    try {
      await navigator.clipboard.writeText(checkoutUrl);
      setIsCopied(true);
      window.setTimeout(() => setIsCopied(false), 2500);
    } catch {
      alert('Não foi possível copiar o link de pagamento.');
    } finally {
      setIsCopying(false);
    }
  }, [checkoutUrl, isCopying]);

  if (!open) {
    return null;
  }

  const selectedPlan = plans.find((plan) => String(plan.id) === selectedPlanId) ?? null;
  const summaryName = lockPlan
    ? (lockedPlan?.name || selectedPlan?.name || 'Plano da assinatura')
    : (selectedPlan?.name ?? '');
  const summaryPriceCents = linkMode === 'pay_existing' && openPaymentValue !== null
    ? Math.round(openPaymentValue * 100)
    : lockPlan
      ? (lockedPlan?.priceCents ?? selectedPlan?.price_cents ?? null)
      : (selectedPlan?.price_cents ?? null);
  const summaryCycle = lockPlan
    ? (lockedPlan?.billingCycle || selectedPlan?.billing_cycle || '')
    : (selectedPlan?.billing_cycle ?? '');
  const summaryIsSinglePayment = lockPlan
    ? (lockedPlan?.isSinglePayment ?? selectedPlan?.is_single_payment ?? false)
    : (selectedPlan?.is_single_payment ?? false);
  const summaryCycleNormalized = summaryCycle.trim().toLowerCase();
  const summaryCycleLabel = summaryCycle
    ? CYCLE_LABELS[summaryCycleNormalized] ?? CYCLE_LABELS[summaryCycle] ?? summaryCycle
    : '';
  const showPlanSummary = lockPlan
    ? Boolean(selectedPlanId)
    : Boolean(selectedPlan);
  const copyIconName = isCopying ? 'progress_activity' : isCopied ? 'done' : 'content_copy';

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(event) => event.stopPropagation()}>
        <div className={styles['icon-circle']}>
          <span className="material-symbols-outlined">payments</span>
        </div>

        <h3 className={styles.title}>Link de pagamento</h3>
        <p className={styles.message}>
          {linkMode === 'pay_existing'
            ? `Checkout para pagar a cobrança pendente/vencida${studentName ? ` de ${studentName}` : ''}.`
            : `Checkout resumido para cliente Asaas existente${studentName ? ` (${studentName})` : ''}. Sem cadastro — só pagar.`}
        </p>

        {linkError && (
          <div className={styles.notice} role="status">
            <span className="material-symbols-outlined">info</span>
            <p>{linkError}</p>
          </div>
        )}

        {!lockPlan && (
          <div className={styles['select-wrap']}>
            {plansLoading ? (
              <Loading />
            ) : (
              <FormSelect
                id="charge-checkout-plan"
                label="Plano de assinatura"
                options={plans.map((plan) => ({ value: String(plan.id), label: plan.name }))}
                value={selectedPlanId}
                onChange={(event) => setSelectedPlanId(event.target.value)}
                disabled={plans.length === 0}
                {...createPlanSelectFormatters(plans)}
              />
            )}
          </div>
        )}

        {showPlanSummary && (
          <div className={styles['plan-summary']}>
            {lockPlan && (
              <div className={styles['plan-summary-row']}>
                <span className={styles['plan-summary-label']}>Plano</span>
                <span className={styles['plan-summary-value']}>
                  <PlanNameWithProject
                    name={summaryName || '—'}
                    project={lockedPlan?.project ?? selectedPlan?.project}
                  />
                </span>
              </div>
            )}
            <div className={styles['plan-summary-row']}>
              <span className={styles['plan-summary-label']}>Valor</span>
              <span className={styles['plan-summary-value']}>
                {summaryPriceCents !== null
                  ? formatCurrency(summaryPriceCents / 100)
                  : '—'}
                {linkMode !== 'pay_existing' && summaryCycleLabel
                  ? `/${summaryCycleLabel.toLowerCase()}`
                  : ''}
              </span>
            </div>
            {linkMode !== 'pay_existing' && (
              <>
                <div className={styles['plan-summary-row']}>
                  <span className={styles['plan-summary-label']}>Ciclo</span>
                  <span className={styles['plan-summary-value']}>{summaryCycleLabel || '—'}</span>
                </div>
                <div className={styles['plan-summary-row']}>
                  <span className={styles['plan-summary-label']}>À vista</span>
                  <span className={styles['plan-summary-value']}>
                    {summaryIsSinglePayment ? 'Sim' : 'Não'}
                  </span>
                </div>
              </>
            )}
            {linkMode === 'pay_existing' && (
              <div className={styles['plan-summary-row']}>
                <span className={styles['plan-summary-label']}>Tipo</span>
                <span className={styles['plan-summary-value']}>Cobrança pendente/vencida</span>
              </div>
            )}
          </div>
        )}

        {selectedPlanId && !linkError && (
          <div className={styles['url-wrap']}>
            <FormInput
              id="charge-checkout-url"
              label="URL do pagamento"
              value={urlLoading ? 'Gerando link...' : checkoutUrl}
              readOnly
              loading={urlLoading}
              endAdornment={(
                <Tooltip content="Copiar link de pagamento">
                  <button
                    type="button"
                    className={styles['copy-btn']}
                    onClick={() => {
                      void handleCopy();
                    }}
                    aria-label="Copiar link de pagamento"
                    disabled={!checkoutUrl || urlLoading || isCopying}
                  >
                    <span
                      className={`material-symbols-outlined ${isCopying ? styles.spinner : ''}`.trim()}
                    >
                      {copyIconName}
                    </span>
                  </button>
                </Tooltip>
              )}
            />
          </div>
        )}

        <div className={styles.actions}>
          <button
            type="button"
            className={`${styles.btn} ${styles['btn-close']}`}
            onClick={onClose}
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChargeCheckoutLinkModal;
